import router from '#/router'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getChargeProducts, createOrder, verifyReceipt, getUser, getChargeProductIten } from "#/api/index.js"
import { toastSuccess, toastError } from "#/utils/toast"
import { loadScript } from "@paypal/paypal-js"
import * as Airwallex from '@airwallex/components-sdk'


export function useOumiRecharge() {
  const { t } = useI18n()

  const loading = ref(false)
  const productsLoading = ref(true)
  const select_recharge_id = ref(1)
  const select_recharge_item = ref()
  const paypal_product_item = ref()
  const recharge_list = ref([])
  const supportedPayTypes = ref([])
  const user = ref(null)
  const user_id = ref('')
  const isNewUser = ref(false)
  const clickPayItem = ref(null)
  const isTest = import.meta.env.VITE_NODE_ENV !== 'production'

  var chargeUid = 0
  let orderMap = {}

  // 判断支付方式是否可用
  function isPaymentMethodAvailable(payType) {
    if (!select_recharge_item.value) return false
    
    // 获取实际美元价格
    const priceInDollars = select_recharge_item.value.coin / 50
    
    // Airwallex 需要50美元起步
    if (payType === 'AIRWALLEX') {
      return priceInDollars >= 50
    }
    
    // 其他支付方式20美元起步
    return priceInDollars >= 20
  }

  async function airwallexPayClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    try {
      clickPayItem.value = "AIRWALLEX"

      var requestBody = {
        product_id: select_recharge_id.value,
        pay_item_type: "AIRWALLEX",
        user_id: user.value.userId,
        v2: true,
        extra: JSON.stringify({
          return_url: window.location.origin + '/recharge'
        })
      }
      loading.value = true

      const rsp = await createOrder(requestBody)
      
      var airwallexInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
      console.log(airwallexInfo)
      const { payments } = await Airwallex.init({
          env: isTest ? 'demo' : 'prod', // Setup which Airwallex env('staging' | 'demo' | 'prod') to integrate with
          enabledElements: ['payments']})
      
      payments.redirectToCheckout({
        env: isTest ? 'demo' : 'prod',
        mode: 'payment',
        currency: 'USD', 
        intent_id: airwallexInfo.intentId,
        client_secret: airwallexInfo.client_secret,
        successUrl: window.location.origin + `/recharge?uid=${user.value.userId}&airwallex=1&orderId=${rsp.data.orderId}`,
        failUrl: window.location.origin + '/recharge?airwallex=1',
        appearance: {
              mode: 'dark', // choose between 'dark' and 'light'
              variables: {
                colorBrand: '#612FFF',
              },
            }
      })

      loading.value = false
      clickPayItem.value = null

    } catch (error) {
      toastError(t('recharge.errors.topUpFailed'))
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  function priceChangeHandle(item) {
    select_recharge_id.value = item.id
    select_recharge_item.value = item
    loadPaypalProductItem()
  }

  async function loadPaypalProductItem() {
    let res = await getChargeProductIten(select_recharge_id.value, "PAYPAL", user_id.value)
    if (res && res.success) {
      paypal_product_item.value = res.data
    }
  }

  async function getProductList() {
    productsLoading.value = true
    try {
      let res = await getChargeProducts(user_id.value)
      if (res && res.success) {
        recharge_list.value = res.data.product.filter(item => {
          // 计算美元价格（通过 cent 或 coin / 50）
          const priceInDollars = item.cent ? parseFloat(item.cent) : (item.coin / 50)

          // 只保留价格 >= 20 美元的产品
          return priceInDollars >= 20
        });
        supportedPayTypes.value = res.data.supportedPayTypes
        if (recharge_list.value.length > 0) {
          select_recharge_id.value = recharge_list.value[0].id
          select_recharge_item.value = recharge_list.value[0]
          loadPaypalProductItem()
        }
      }
    } finally {
      productsLoading.value = false
    }
  }

  function checkUid(e) {
    let str = e.target.value;
    if (str[0] == 0) {
      user_id.value = str.substr(1);
    }
    user_id.value = user_id.value + '';
    user_id.value = user_id.value.replace(/[^\d]/g, '')
  }

  async function saveUid(e) {
    if (user.value && user.value == e.target.value) {
      return
    }
    if (e.target.value != null && e.target.value.length > 0) {
      chargeUid = e.target.value
      try {
        const res = await getUser({ user_id: chargeUid })
        if (res.success) {
          user.value = res.data;
          isNewUser.value = Boolean(res.data?.isNewUser ?? res.isNewUser)
        } else {
          user.value = null
          isNewUser.value = false
          toastError(res.message)
        }
      } finally {
        await getProductList()
      }
    } else {
      user.value = null
      isNewUser.value = false
      await getProductList()
    }
  }

  async function clearUserInput() {
    user_id.value = ''
    user.value = null
    isNewUser.value = false
    await getProductList()
  }

  async function getinfoByUid(uid) {
    chargeUid = uid
    if (chargeUid) {
      let res = await getUser({ user_id: chargeUid });
      if (res.success) {
        user.value = res.data;
        isNewUser.value = Boolean(res.data?.isNewUser ?? res.isNewUser)
      } else {
        user.value = null
        isNewUser.value = false
        toastError(res.message)
      }
    }
  }

  async function stripePayClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    try {
      clickPayItem.value = "STRIPE"

      var requestBody = {
        product_id: select_recharge_id.value,
        pay_item_type: "STRIPE",
        user_id: user.value.userId,
        v2: true,
        extra: JSON.stringify({
          return_url: window.location.origin + '/recharge'
        })
      }
      loading.value = true

      const rsp = await createOrder(requestBody)

      loading.value = false
      clickPayItem.value = null

      sessionStorage.setItem('__stp.od', JSON.stringify(rsp.data))
      sessionStorage.setItem('uid', user.value.userId)

      var stripeInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)

      router.push({ name: 'stripe', state: { stripeInfo } })
    } catch (error) {
      toastError(t('recharge.errors.topUpFailed'))
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  async function checkStripeSession(sessionId) {
    try {
      let stripeOrder = JSON.parse(sessionStorage.getItem('__stp.od'))
      let uid = sessionStorage.getItem('uid')
      if (!stripeOrder || !uid) {
        window.location.href = window.location.origin + window.location.pathname;
        return
      }

      var requestBody = {
        order_id: stripeOrder.orderId,
        user_id: uid,
        extra_json: JSON.stringify({
          session_id: sessionId,
          packageName: "yay.chat.app"
        })
      }

      loading.value = true

      const rsp = await verifyReceipt(requestBody);

      loading.value = false
      sessionStorage.removeItem('__stp.od')
      if (rsp.success || rsp.code == 1001510117) {
        toastSuccess(t('recharge.success.topUpSuccessful'))
        window.location.href = window.location.origin + window.location.pathname + `?uid=${uid}`;
      } else {
        toastError(rsp.message ?? t('recharge.errors.topUpFailed'))
      }
    } catch (e) {
      toastError(e.toString())
    } finally {
      loading.value = false
    }
  }

  // PayerMax 支付
  async function payermaxClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    clickPayItem.value = "PAYERMAX"

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: "PAYERMAX",
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        front_callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      if (rsp.success) {
        var payermaxInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        window.location.href = payermaxInfo.redirectUrl
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  // DIANDIAN 支付
  async function diandianPayClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    clickPayItem.value = "DIANDIAN"

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: "DIANDIAN",
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      if (rsp.success) {
        var payInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        window.location.href = payInfo.payLink
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  // PingPong 支付
  async function pingpongClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    clickPayItem.value = "PINGPONG"

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: "PINGPONG",
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        front_callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      console.log(rsp)
      if (rsp.success) {
        var pingpongInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        console.log('pingpongInfo', pingpongInfo)
        if(pingpongInfo && pingpongInfo.bizContent){
          var bizContent = JSON.parse(pingpongInfo.bizContent)
          if(bizContent && bizContent.paymentUrl){
            window.location.href = bizContent.paymentUrl
            return
          }
        }
        
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  // PayPal 支付
  async function customPaypalClick() {
    const paypalOrderInfo = await createPaypalOrder()
    if (paypalOrderInfo) {
      const link = paypalOrderInfo.links.find((item) => item.rel == 'payer-action')
      if (link) {
        window.location.href = link.href
      }
    }
  }

  async function createPaypalOrder() {
    try {
      if (!user.value) {
        toastError(t('recharge.errors.enterUserToRecharge'))
        return null;
      }

      var requestBody = {
        product_id: select_recharge_id.value,
        pay_item_type: "PAYPAL",
        user_id: user.value.userId,
        v2: true,
        extra: JSON.stringify({
          return_url: window.location.origin + '/recharge',
          cancel_url: window.location.origin + '/recharge'
        })
      }

      loading.value = true
      const orderRsp = await createOrder(requestBody)
      loading.value = false

      if (orderRsp.success) {
        let bizOrderId = orderRsp.data.orderId;
        var paypalOrderInfo = JSON.parse(orderRsp.data.payInfo.payInfoExtra.payExtra)
        orderMap[paypalOrderInfo.id] = orderRsp.data.orderId
        return paypalOrderInfo;
      } else {
        toastError(orderRsp.message ?? t('recharge.errors.createOrderFailed'))
        return null;
      }
    } catch (error) {
      console.error(error);
      toastError(t('recharge.errors.paypalOrderCreationFailed'))
      return null;
    } finally {
      loading.value = false
    }
  }

  // 验证 PayerMax 支付
  async function checkPayermax(uid, orderId, tradeNo) {
    try {
      if (!tradeNo || !orderId || !uid) {
        window.location.href = window.location.origin + window.location.pathname;
        return
      }

      var requestBody = {
        order_id: orderId,
        user_id: uid,
        extra_json: JSON.stringify({
          outTradeNo: tradeNo,
          packageName: "yay.chat.app"
        })
      }

      loading.value = true
      const rsp = await verifyReceipt(requestBody);
      loading.value = false

      if (rsp.success || rsp.code == 1001510117) {
        toastSuccess(t('recharge.success.topUpSuccessful'))
        setTimeout(() => {
          window.location.href = window.location.origin + window.location.pathname + `?uid=${uid}`;
        }, 1000)
      } else {
        toastError(rsp.message ?? t('recharge.errors.topUpFailed'))
      }
    } catch (e) {
      toastError(e.toString())
    } finally {
      loading.value = false
    }
  }

  // 验证 PayPal 支付
  async function checkPaypal(yayOrderId, uid, paypalOid) {
    try {
      var requestBody = {
        order_id: yayOrderId,
        user_id: uid,
        extra_json: JSON.stringify({
          paypalOrderId: paypalOid,
          packageName: "yay.chat.app"
        })
      }

      loading.value = true
      const rsp = await verifyReceipt(requestBody);
      loading.value = false

      if (rsp.success) {
        toastSuccess(t('recharge.success.topUpSuccessful'))
        setTimeout(() => {
          window.location.href = window.location.origin + window.location.pathname + `?uid=${uid}`;
        }, 1000)
      } else {
        toastError(t('recharge.errors.topUpFailed'))
      }
    } catch (error) {
      console.error(error);
      toastError(t('recharge.errors.paymentVerificationFailed'))
    } finally {
      loading.value = false
    }
  }

  // 验证 DIANDIAN 支付
  async function checkDiandianPay(uid, orderId, ddid) {
    try {
      if (!ddid || !orderId || !uid) {
        window.location.href = window.location.origin + window.location.pathname;
        return
      }

      var requestBody = {
        order_id: orderId,
        user_id: uid,
        extra_json: JSON.stringify({
          diandianId: ddid,
          packageName: "yay.chat.app"
        })
      }

      loading.value = true
      const rsp = await verifyReceipt(requestBody);
      loading.value = false

      if (rsp.success || rsp.code == 1001510117) {
        toastSuccess(t('recharge.success.topUpSuccessful'))
        setTimeout(() => {
          window.location.href = window.location.origin + window.location.pathname + `?uid=${uid}`;
        }, 1000)
      } else {
        toastError(rsp.message ?? t('recharge.errors.topUpFailed'))
      }
    } catch (e) {
      toastError(e.toString())
    } finally {
      loading.value = false
    }
  }

  // 验证 Airwallex 支付
  async function checkAirwallexPay(uid, orderId, intentId) {
    try {
      if (!intentId || !orderId || !uid) {
        window.location.href = window.location.origin + window.location.pathname;
        return
      }

      var requestBody = {
        order_id: orderId,
        user_id: uid,
        extra_json: JSON.stringify({
          id: intentId,
          packageName: "yay.chat.app"
        })
      }

      loading.value = true
      const rsp = await verifyReceipt(requestBody);
      loading.value = false

      if (rsp.success || rsp.code == 1001510117) {
        toastSuccess(t('recharge.success.topUpSuccessful'))
        setTimeout(() => {
          window.location.href = window.location.origin + window.location.pathname + `?uid=${uid}`;
        }, 1000)
      } else {
        toastError(rsp.message ?? t('recharge.errors.topUpFailed'))
      }
    } catch (e) {
      toastError(e.toString())
    } finally {
      loading.value = false
    }
  }

  // HAIPAY_USDT 支付
  async function haipayClick() {
    if (!user.value) {
      toastError(t('recharge.errors.enterUserToRecharge'))
      return 0;
    }

    if (clickPayItem.value) {
      toastError(t('recharge.errors.pleaseWait'))
      return;
    }

    clickPayItem.value = "HAIPAY_USDT"

    var requestBody = {
      product_id: select_recharge_id.value,
      pay_item_type: "HAIPAY_USDT",
      user_id: user.value.userId,
      v2: true,
      extra: JSON.stringify({
        callback_url: window.location.origin + '/recharge'
      })
    }

    try {
      loading.value = true
      const rsp = await createOrder(requestBody)
      
      clickPayItem.value = null
      loading.value = false

      if (rsp.success) {
        var haipayInfo = JSON.parse(rsp.data.payInfo.payInfoExtra.payExtra)
        window.location.href = haipayInfo.payUrl
      } else {
        toastError(rsp.message ?? t('recharge.errors.createOrderFailed'))
      }
    } catch (e) {
      toastError(e)
    } finally {
      loading.value = false
      clickPayItem.value = null
    }
  }

  onMounted(async () => {
    let queryValue = router.currentRoute.value.query
    const queryUid = Array.isArray(queryValue.uid) ? queryValue.uid[0] : queryValue.uid
    const callbackUid = queryValue.session_id ? sessionStorage.getItem('uid') : ''
    const normalizedUid = `${queryUid || callbackUid || ''}`.replace(/[^\d]/g, '')

    user_id.value = normalizedUid
    await getProductList()
    if (normalizedUid) {
      await getinfoByUid(normalizedUid)
    }
    
    // 检查各种支付回调
    let sessionId = queryValue.session_id
    let isPayerMax = queryValue.payermax == 1
    let isPaypal = queryValue.paypal == 1
    let isDiandianPay = queryValue.diandianpay == 1
    let isAirwallex = queryValue.airwallex == 1
    let isPingPong = queryValue.pingpong == 1
    
    if (sessionId) {
      let uid = sessionStorage.getItem('uid')
      user_id.value = uid
      loading.value = true
      checkStripeSession(sessionId)
    } else if (isPayerMax) {
      const status = queryValue.status
      if (status && status.toUpperCase() == 'SUCCESS') {
        user_id.value = queryValue.uid
        let orderId = queryValue.orderId
        let tradeNo = queryValue.outTradeNo
        loading.value = true
        checkPayermax(user_id.value, orderId, tradeNo)
      }
    } else if (isPaypal) {
      const state = queryValue.state
      if (queryValue.token && state == 0) {
        checkPaypal(queryValue.yay_oid, queryValue.uid, queryValue.token)
      } else {
        toastError(t('recharge.errors.topUpFailed'))
      }
    } else if (isDiandianPay) {
      user_id.value = queryValue.uid
      let orderId = queryValue.orderId
      const ddid = queryValue.ddid
      loading.value = true
      checkDiandianPay(user_id.value, orderId, ddid)
    }else if(isAirwallex) {
      user_id.value = queryValue.uid
      let orderId = queryValue.orderId
      const intentId = queryValue.id
      if(intentId) {
        loading.value = true
        checkAirwallexPay(user_id.value,orderId, intentId)
      } else {
        toastError(t('recharge.errors.topUpFailed'))
      }
    }
  })

  return {
    loading,
    productsLoading,
    select_recharge_id,
    select_recharge_item,
    paypal_product_item,
    recharge_list,
    supportedPayTypes,
    user,
    user_id,
    isNewUser,
    clickPayItem,
    isPaymentMethodAvailable,
    airwallexPayClick,
    priceChangeHandle,
    checkUid,
    saveUid,
    clearUserInput,
    stripePayClick,
    payermaxClick,
    diandianPayClick,
    pingpongClick,
    customPaypalClick,
    haipayClick,
  }
}
